import {
  getFirebaseApp,
  getFunctionsRegion,
  isFirebaseConfigured,
} from "./firebase";
import { getCurrentUser, hasAccount } from "./auth";
import { parseCapture } from "./productCapture";
import type {
  ImportedProduct,
  MaterialId,
  ParamValue,
  ProductCapture,
  ProductKind,
  TemplateId,
} from "./types";
import {
  AiQuotaExceededError,
  isQuotaExceededError,
  recordOptimisticUsage,
  type UsageResponseEnvelope,
} from "./usage";
import { captureException, track } from "./telemetry";
import {
  creditsRemaining,
  isNetworkCallableError,
  noteQuotaReached,
  noteUsageEnvelope,
} from "./aiAnalytics";
import {
  normalizeSourceDomain,
  productUrlForAnalytics,
  sourceDomainForAnalytics,
} from "./productUrlAnalytics";

/** Re-export so the import sheet can import the typed quota error from
 *  the same module that throws it. */
export { AiQuotaExceededError } from "./usage";

/**
 * Client-side wrapper around the `importProductFromUrl` Cloud Function.
 *
 * Mirrors the pre-flight / typed-error pattern used by
 * {@link import("./fengShui").requestFengShuiLayout}:
 *
 *   - Throws {@link ProductImportUnavailableError} when Firebase isn't
 *     configured for this environment (e.g. a contributor cloning the
 *     repo with no `.env.local`). The UI can render an inline message
 *     instead of a wire-level crash.
 *   - Throws {@link ProductImportSignInRequiredError} when the user has
 *     no registered account (signed out or an anonymous session). The
 *     Cloud Function would reject the call anyway; we short-circuit so
 *     the UI can show a sign-in prompt without burning a model call.
 *   - Throws {@link ProductImportInvalidUrlError} when the input
 *     string isn't a valid URL on the client side.
 *
 * Anything else is rethrown verbatim — the Cloud Function uses
 * `HttpsError` so the caller sees a `FirebaseError` with `.code` like
 * `functions/invalid-argument`.
 */

/** Wire shape returned by the `importProductFromUrl` callable. Mirrors
 *  {@link ImportProductResponseSchema} in `functions/src/productImport.ts`
 *  plus the `sourceUrl` / `sourceDomain` the server enriches the
 *  response with. */
export interface ExtractedProduct {
  name: string;
  kind: ProductKind;
  width: number;
  depth: number;
  height: number;
  category: ImportedProduct["category"];
  color: string;
  notes?: string;
  needsManualReview: boolean;
  metadata?: ImportedProduct["metadata"];
  /** Closest parametric template (furniture only). */
  template?: TemplateId;
  templateParams?: Record<string, ParamValue>;
  material?: MaterialId;
  /** Representative hex colors, primary first (max 3). */
  palette?: string[];
  /** Materials called out by the listing, primary first (max 3). */
  materials?: MaterialId[];
  dimensionSource?: "listing" | "estimated";
  sourceUrl: string;
  sourceDomain: string;
  /** The retailer's own mesh, stored at import time (furniture only). */
  capture?: ProductCapture;
  /** No usable retailer mesh. `galleryUrls` are the listing photos to
   *  rebuild one from; absent when there are none worth sending. */
  modelFallback?: { galleryUrls?: string[] };
  /** How the retailer-mesh attempt went (furniture only). */
  officialModel?: { reason: OfficialModelReason; latencyMs: number };
  /** The import's idempotency key. `reconstructProductModel` only runs
   *  for a request this account already paid for. Set by the client. */
  requestId?: string;
}

export type OfficialModelReason = "ready" | "missing" | "fetch_failed" | "invalid" | "timeout";
const OFFICIAL_MODEL_REASONS: readonly OfficialModelReason[] = [
  "ready",
  "missing",
  "fetch_failed",
  "invalid",
  "timeout",
];

/** Matches `MAX_GALLERY_URLS` and the reconstruction request schema in
 *  `functions/src/productImport.ts` / `reconstructHandler.ts`. */
const MAX_GALLERY_URLS = 8;
const MAX_GALLERY_URL_LENGTH = 2048;

/**
 * Best-effort template from a product name. Used when placing an older
 * imported listing that predates the parametric extractor (no `template`
 * field) so a "Corner sofa" still renders as an L instead of a box.
 */
export function templateFromProductName(name: string): TemplateId {
  const rules: [RegExp, TemplateId][] = [
    [/\b((?:kitchen|prep)\s+islands?|islands?\s+with\s+seating)\b/i, "island"],
    [/\b(countertops?|counters?|vanit(?:y|ies)|bar\s+tops?)\b/i, "counter"],
    [/\b(corner sofa|sectional|l[- ]shaped|chaise sectional)\b/i, "sectional"],
    [/\b(loveseat|sofa|couch)\b/i, "sofa"],
    [/\barm\s*chairs?\b|\baccent chair\b|\blounge chair\b/i, "armchair"],
    [/\b(bar[- ]?)?stools?\b/i, "stool"],
    [/\b(ottoman|pouf)\b/i, "ottoman"],
    [/\bbench(es)?\b/i, "bench"],
    [/\bdesks?\b/i, "desk"],
    [/\b(nightstands?|night tables?|bedside)\b/i, "nightstand"],
    [/\b(dressers?|chest of drawers)\b/i, "dresser"],
    [/\b(wardrobes?|armoires?)\b/i, "wardrobe"],
    [/\b(bookshelves|bookshelf|bookcases?)\b/i, "bookshelf"],
    [/\b(tv[- ]?stands?|media consoles?)\b/i, "tv-stand"],
    [/\b(fridges?|refrigerators?)\b/i, "fridge"],
    [/\b(washers?|washing machines?)\b/i, "washer"],
    [/\b(stoves?|ranges?)\b/i, "stove"],
    [/\brugs?\b/i, "rug"],
    [/\blamps?\b/i, "lamp"],
    [/\bplants?\b/i, "plant"],
    [/\bbeds?\b/i, "bed"],
    [/\btables?\b/i, "table"],
    [/\bchairs?\b/i, "chair"],
  ];
  for (const [re, id] of rules) {
    if (re.test(name)) return id;
  }
  return "box";
}

export class ProductImportUnavailableError extends Error {
  constructor() {
    super(
      "Product import is unavailable: Firebase is not configured. Set VITE_FIREBASE_* env vars."
    );
    this.name = "ProductImportUnavailableError";
  }
}

/** Thrown when the caller has no registered account. The backend rejects
 *  anonymous sessions and signed-out callers (see `functions/src/auth.ts`);
 *  we mirror the check on the client so the UI can render an inline
 *  "Sign in" prompt instead of an opaque wire-level error. */
export class ProductImportSignInRequiredError extends Error {
  constructor() {
    super("Sign in to import products from a URL.");
    this.name = "ProductImportSignInRequiredError";
  }
}

/** Thrown when the backend rejects with `permission-denied` — the caller
 *  is signed in but isn't Premium. The UI should re-pop the premium
 *  upsell overlay rather than the sign-in prompt. */
export class ProductImportPremiumRequiredError extends Error {
  constructor() {
    super("Upgrade to Premium to import products from a URL.");
    this.name = "ProductImportPremiumRequiredError";
  }
}

function isFunctionsPermissionDenied(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const code = (err as { code?: unknown }).code;
  return code === "functions/permission-denied" || code === "permission-denied";
}

/** Thrown when the user-supplied URL is malformed or has a protocol we
 *  refuse to send to the server (anything other than `http`/`https`). */
export class ProductImportInvalidUrlError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProductImportInvalidUrlError";
  }
}

/** Thrown when the function responded but the returned payload doesn't
 *  match {@link ExtractedProduct} (defensive — the server already
 *  validates with Zod). */
export class ProductImportExtractionFailedError extends Error {
  constructor() {
    super(
      "The product page couldn't be read. Try a different listing URL."
    );
    this.name = "ProductImportExtractionFailedError";
  }
}

/** Thrown when the callable fails for a non-quota reason (fetch blocked,
 *  timeout, model error). The sheet shows {@link message} inline instead
 *  of closing into the Premium upsell. */
export class ProductImportFailedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProductImportFailedError";
  }
}

export const LISTING_UNREADABLE_MESSAGE =
  "We couldn't read that listing. The site may be blocking automated access — try another URL or create the piece manually.";

/**
 * Map a Firebase callable / network error into copy the import sheet can
 * show. Raw SDK messages like `INTERNAL` or `functions/unavailable` are
 * what made the flow look like "nothing happened."
 */
export function friendlyCallableError(err: unknown): string {
  const { code, message } = firebaseErrorParts(err);
  const lowerCode = code.toLowerCase();
  if (lowerCode.includes("deadline-exceeded")) {
    return "The listing took too long to load. Try again.";
  }
  if (lowerCode.includes("unavailable")) {
    return isHumanImportMessage(message)
      ? message
      : LISTING_UNREADABLE_MESSAGE;
  }
  if (lowerCode.includes("invalid-argument")) {
    return isHumanImportMessage(message)
      ? message
      : "That URL isn't a readable product listing.";
  }
  if (
    lowerCode.includes("internal") ||
    lowerCode.includes("unknown") ||
    lowerCode.includes("deadline")
  ) {
    return isHumanImportMessage(message)
      ? message
      : "Something went wrong reading that listing. Try again.";
  }
  if (isHumanImportMessage(message)) return message;
  return LISTING_UNREADABLE_MESSAGE;
}

function firebaseErrorParts(err: unknown): { code: string; message: string } {
  if (!err || typeof err !== "object") {
    return { code: "", message: err instanceof Error ? err.message : "" };
  }
  const o = err as { code?: unknown; message?: unknown };
  return {
    code: typeof o.code === "string" ? o.code : "",
    message: typeof o.message === "string" ? o.message : "",
  };
}

/** True when `message` looks like something a person can act on, not a
 *  wire-level code (`INTERNAL`, `functions/unavailable`, empty). */
function isHumanImportMessage(message: string): boolean {
  const trimmed = message.trim();
  if (trimmed.length < 8) return false;
  if (/^(functions\/)?[a-z0-9_-]+$/i.test(trimmed)) return false;
  return true;
}

const URL_MAX_LENGTH = 2048;

/**
 * Validate a URL string on the client. Returns the parsed URL on success
 * and throws {@link ProductImportInvalidUrlError} on failure (so the
 * `ImportProductSheet` can surface the message inline).
 *
 * Exported so the input field can validate as the user types without
 * actually invoking the Cloud Function.
 */
export function validateProductUrl(input: string): URL {
  const trimmed = (input ?? "").trim();
  if (trimmed.length === 0) {
    throw new ProductImportInvalidUrlError("Paste a product URL to import.");
  }
  if (trimmed.length > URL_MAX_LENGTH) {
    throw new ProductImportInvalidUrlError(
      `URL is too long (max ${URL_MAX_LENGTH} characters).`
    );
  }
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new ProductImportInvalidUrlError(
      "That doesn't look like a valid URL. Make sure it starts with https://"
    );
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw new ProductImportInvalidUrlError(
      "Only http(s) URLs are supported."
    );
  }
  return parsed;
}

/**
 * Call the Cloud Function and return the model's extracted product.
 * See class-level docstring for the typed-error contract.
 */
export async function importProductFromUrl(
  url: string
): Promise<ExtractedProduct> {
  let parsedUrl: URL;
  try {
    parsedUrl = validateProductUrl(url);
  } catch (err) {
    if (err instanceof ProductImportInvalidUrlError) {
      // Best-effort: the paste may still be URL-parseable even when it
      // failed validation (e.g. an ftp:// scheme). `*ForAnalytics` return
      // undefined for genuinely unparseable input, so the params are
      // simply omitted then.
      track("ai_product_import_failed", {
        reason: "invalid_url",
        ...importUrlParamsFromPaste(url),
      });
    }
    throw err;
  }

  if (!isFirebaseConfigured()) throw new ProductImportUnavailableError();
  const app = getFirebaseApp();
  if (!app) throw new ProductImportUnavailableError();
  if (!hasAccount(getCurrentUser())) throw new ProductImportSignInRequiredError();

  // Dynamic import keeps the Firebase Functions SDK out of the initial
  // bundle (same pattern as `fengShui.ts`).
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const functions = getFunctions(app, getFunctionsRegion());

  // Function timeout is 90s server-side; allow a touch more on the client
  // so we don't abort a healthy late-arriving response.
  const callable = httpsCallable<
    { url: string; requestId: string },
    ExtractedProduct & { usage?: UsageResponseEnvelope }
  >(functions, "importProductFromUrl", { timeout: 100_000 });

  track("ai_product_import_requested", importUrlParamsFromPaste(parsedUrl.toString()));
  const startedAt = Date.now();
  // Idempotency key — server uses it to dedup the credit count + Stripe
  // meter event on retry (see `src/lib/usage.ts`), and the reconstruction
  // callable checks it to confirm this import was paid for.
  const requestId = makeRequestId();
  let res;
  try {
    res = await callable({ url: parsedUrl.toString(), requestId });
  } catch (err) {
    if (isQuotaExceededError(err)) {
      track("ai_product_import_failed", { reason: "quota" });
      noteQuotaReached("free");
      throw new AiQuotaExceededError();
    }
    if (isFunctionsPermissionDenied(err)) {
      track("ai_product_import_failed", { reason: "premium_required" });
      throw new ProductImportPremiumRequiredError();
    }
    if (isNetworkCallableError(err)) {
      track("ai_product_import_failed", { reason: "network" });
      throw new ProductImportFailedError(friendlyCallableError(err));
    }
    track("ai_product_import_failed", { reason: "unknown" });
    captureException(err, { context: "importProductFromUrl" });
    throw new ProductImportFailedError(friendlyCallableError(err));
  }
  const data = res.data;
  // Capture the usage envelope BEFORE narrowing — the type guard below
  // strips it (ExtractedProduct doesn't include `usage`), so reading it
  // after the narrow would lose the field even though the JS object
  // still carries it. Older server deployments omit `usage` entirely;
  // skip the optimistic update in that case (the Firestore snapshot
  // will catch up).
  const usage = data && typeof data === "object" ? data.usage : undefined;
  if (!isExtractedProduct(data)) {
    track("ai_product_import_failed", { reason: "extraction" });
    throw new ProductImportExtractionFailedError();
  }
  if (usage) recordOptimisticUsage(usage);
  const product = withModelFields(data, requestId);
  track("ai_product_import_succeeded", {
    template: product.template ?? null,
    dimension_source: product.dimensionSource ?? null,
    latency_ms: Date.now() - startedAt,
    credits_remaining: creditsRemaining(usage),
    source: product.capture
      ? "official"
      : product.modelFallback?.galleryUrls
        ? "pending"
        : "none",
    // The server resolves the final URL after redirects; use its values
    // rather than the pasted URL so the event reflects the real listing.
    ...importUrlParamsFromServer(product.sourceDomain, product.sourceUrl),
  });
  if (product.officialModel) {
    const { capture, officialModel } = product;
    track("product_model_official_result", {
      reason: officialModel.reason,
      latency_ms: officialModel.latencyMs,
      ...(capture ? { triangles: capture.triangles, bytes: capture.bytes, pbr: capture.pbr } : {}),
    });
  }
  noteUsageEnvelope(usage);
  return product;
}

/**
 * The server's 3D-model fields, narrowed so a malformed or older response
 * can only lose them (the import itself stands), plus the request id.
 * Mounts never carry a model.
 */
function withModelFields(data: ExtractedProduct, requestId: string): ExtractedProduct {
  const { capture, modelFallback, officialModel } = data;
  const product: ExtractedProduct = { ...data, requestId };
  delete product.capture;
  delete product.modelFallback;
  delete product.officialModel;
  delete (product as { usage?: unknown }).usage;
  if (product.kind !== "furniture") return product;
  const parsed = parseCapture(capture);
  if (parsed) product.capture = parsed;
  else if (modelFallback && typeof modelFallback === "object") {
    const galleryUrls = galleryUrlsOf(modelFallback.galleryUrls);
    product.modelFallback = galleryUrls.length ? { galleryUrls } : {};
  }
  if (
    officialModel &&
    OFFICIAL_MODEL_REASONS.includes(officialModel.reason) &&
    typeof officialModel.latencyMs === "number" &&
    officialModel.latencyMs >= 0
  ) {
    product.officialModel = { reason: officialModel.reason, latencyMs: Math.round(officialModel.latencyMs) };
  }
  return product;
}

function galleryUrlsOf(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((u): u is string => typeof u === "string" && u.length <= MAX_GALLERY_URL_LENGTH && /^https:\/\//i.test(u))
    .slice(0, MAX_GALLERY_URLS);
}

export type ReconstructReason = "ready" | "no_images" | "vendor" | "invalid" | "timeout" | "cap";
const RECONSTRUCT_REASONS: readonly ReconstructReason[] = [
  "ready",
  "no_images",
  "vendor",
  "invalid",
  "timeout",
  "cap",
];

export interface ReconstructRequest {
  /** The paid import's request id. */
  requestId: string;
  galleryUrls: string[];
  /** Listing box, inches. */
  width: number;
  depth: number;
  height: number;
}

export interface ReconstructOutcome {
  capture: ProductCapture | null;
  reason: ReconstructReason;
  /** Photos the server sent to the vendor. */
  imageCount: number;
}

/** The callable runs up to 180 s (the vendor queue plus the bake). */
const RECONSTRUCT_TIMEOUT_MS = 185_000;

/**
 * Ask the `reconstructProductModel` Cloud Function to rebuild a mesh from
 * the listing photos. Never throws: every failure is an outcome, and the
 * product keeps its parametric piece.
 */
export async function reconstructProductModel(
  request: ReconstructRequest
): Promise<ReconstructOutcome> {
  track("product_model_reconstruct_started", { image_count: request.galleryUrls.length });
  const startedAt = Date.now();
  const outcome = await callReconstruct(request);
  const { capture } = outcome;
  track("product_model_reconstruct_result", {
    reason: outcome.reason,
    latency_ms: Date.now() - startedAt,
    image_count: outcome.imageCount,
    ...(capture ? { triangles: capture.triangles, bytes: capture.bytes, pbr: capture.pbr } : {}),
  });
  return outcome;
}

async function callReconstruct(request: ReconstructRequest): Promise<ReconstructOutcome> {
  const failed = (reason: ReconstructReason): ReconstructOutcome => ({
    capture: null,
    reason,
    imageCount: 0,
  });
  const app = isFirebaseConfigured() ? getFirebaseApp() : null;
  if (!app || !getCurrentUser()) return failed("vendor");
  try {
    const { getFunctions, httpsCallable } = await import("firebase/functions");
    const callable = httpsCallable<ReconstructRequest, unknown>(
      getFunctions(app, getFunctionsRegion()),
      "reconstructProductModel",
      { timeout: RECONSTRUCT_TIMEOUT_MS }
    );
    const res = await callable(request);
    return parseReconstructOutcome(res.data) ?? failed("vendor");
  } catch (err) {
    if (firebaseErrorParts(err).code.toLowerCase().includes("deadline-exceeded")) {
      return failed("timeout");
    }
    if (!isNetworkCallableError(err)) {
      captureException(err, { context: "reconstructProductModel" });
    }
    return failed("vendor");
  }
}

function parseReconstructOutcome(v: unknown): ReconstructOutcome | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const reason = o.reason as ReconstructReason;
  if (!RECONSTRUCT_REASONS.includes(reason)) return null;
  const imageCount =
    typeof o.imageCount === "number" && Number.isFinite(o.imageCount) && o.imageCount >= 0
      ? Math.round(o.imageCount)
      : 0;
  if (reason !== "ready") return { capture: null, reason, imageCount };
  const capture = parseCapture(o.capture);
  return capture
    ? { capture, reason, imageCount }
    : { capture: null, reason: "invalid", imageCount };
}

/** The `source_domain` / `product_url` analytics params for the events we
 *  only know from the pasted URL (`requested`, and the `invalid_url`
 *  failure). Each key is omitted when the paste can't produce it. */
function importUrlParamsFromPaste(
  url: string
): { source_domain?: string; product_url?: string } {
  return compactUrlParams(sourceDomainForAnalytics(url), productUrlForAnalytics(url));
}

/** Same params, but sourced from the server's resolved listing (`succeeded`).
 *  The server returns the post-redirect hostname and URL. */
function importUrlParamsFromServer(
  sourceDomain: string | undefined,
  sourceUrl: string | undefined
): { source_domain?: string; product_url?: string } {
  return compactUrlParams(
    normalizeSourceDomain(sourceDomain),
    productUrlForAnalytics(sourceUrl)
  );
}

function compactUrlParams(
  source_domain: string | undefined,
  product_url: string | undefined
): { source_domain?: string; product_url?: string } {
  const params: { source_domain?: string; product_url?: string } = {};
  if (source_domain) params.source_domain = source_domain;
  if (product_url) params.product_url = product_url;
  return params;
}

/** Per-request idempotency key. Crypto-grade UUID where available,
 *  Math.random fallback for environments without `crypto`. */
function makeRequestId(): string {
  if (
    typeof globalThis !== "undefined" &&
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.randomUUID === "function"
  ) {
    return globalThis.crypto.randomUUID();
  }
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/** Narrow an `unknown` to {@link ExtractedProduct}. Lenient on optional
 *  fields, strict on the required ones. */
function isExtractedProduct(v: unknown): v is ExtractedProduct {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.name === "string" &&
    (o.kind === "furniture" || o.kind === "mount") &&
    typeof o.width === "number" &&
    typeof o.depth === "number" &&
    typeof o.height === "number" &&
    typeof o.category === "string" &&
    typeof o.color === "string" &&
    typeof o.needsManualReview === "boolean" &&
    typeof o.sourceUrl === "string" &&
    typeof o.sourceDomain === "string"
  );
}
